import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet } from "lucide-react";
import { financialService, SalaryAdvance } from "../services/financialService";
import { usePermissions } from "../hooks/usePermissions";

const money = (value: string | number) => `UGX ${Number(value).toLocaleString()}`;

/** What each stage means to the person who asked. */
const STAGE_LABELS: Record<SalaryAdvance["stage"], string> = {
  awaiting_approval: "Waiting for approval",
  approved: "Approved — waiting to be paid out",
  payout_pending: "Being paid out",
  paid_out: "Paid out — comes off your next payslip",
  recovered: "Taken back from your pay",
  rejected: "Turned down",
  cancelled: "Withdrawn",
};

function errorText(failure: unknown) {
  const data = (failure as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== "object") return "The request could not be sent.";
  return Object.values(data as Record<string, unknown>)
    .flat()
    .map(String)
    .join(" ");
}

/**
 * An employee asking for part of next month's pay early. Shown only to staff,
 * on the one page every signed-in person can reach.
 */
export default function SalaryAdvanceCard() {
  const { hasCapability } = usePermissions();
  const isEmployee = hasCapability("access.core.any_employee");
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const position = useQuery({
    queryKey: ["salary-advance-position"],
    queryFn: () => financialService.getSalaryAdvancePosition(),
    enabled: isEmployee,
    retry: false,
  });
  const advances = useQuery({
    queryKey: ["salary-advances"],
    queryFn: financialService.getSalaryAdvances,
    enabled: isEmployee,
  });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["salary-advance-position"] }),
      queryClient.invalidateQueries({ queryKey: ["salary-advances"] }),
    ]);
  const request = useMutation({
    mutationFn: financialService.requestSalaryAdvance,
    onSuccess: () => {
      setNotice("Your request has gone for approval.");
      refresh();
    },
    onError: (failure) => setError(errorText(failure)),
  });
  const cancel = useMutation({
    mutationFn: financialService.cancelSalaryAdvance,
    onSuccess: refresh,
    onError: (failure) => setError(errorText(failure)),
  });

  // Not staff, or no staff record to measure pay against: nothing to offer.
  if (!isEmployee || position.isError) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const form = new FormData(event.currentTarget);
    request.mutate(
      { amount: String(form.get("amount")), reason: String(form.get("reason")).trim() },
      { onSuccess: () => event.currentTarget?.reset() },
    );
  };

  const available = Number(position.data?.available ?? 0);

  return (
    <section className="card">
      <div className="card-content space-y-3">
        <h2 className="flex items-center gap-2 font-semibold">
          <Wallet className="h-5 w-5" />
          Salary advance
        </h2>
        {position.data && (
          <dl className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
            <dt className="text-gray-500">Monthly take-home pay</dt>
            <dd>{money(position.data.net_pay)}</dd>
            <dt className="text-gray-500">Most you can borrow (40%)</dt>
            <dd>{money(position.data.limit)}</dd>
            <dt className="text-gray-500">Already owed or requested</dt>
            <dd>{money(position.data.already_owed_or_requested)}</dd>
            <dt className="font-medium text-gray-700">You can still ask for</dt>
            <dd className="font-medium">{money(available)}</dd>
          </dl>
        )}
        <p className="text-xs text-gray-500">
          An advance is part of next month's pay, handed over early. Once it is paid out, the
          whole amount comes off your next payslip.
        </p>
        {error && <div className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {notice && <div className="rounded bg-green-50 p-3 text-sm text-green-700">{notice}</div>}
        {available > 0 && (
          <form onSubmit={submit} className="space-y-2">
            <input
              required
              name="amount"
              type="number"
              min="1"
              max={available}
              step="1"
              className="input"
              placeholder={`Amount, up to ${available.toLocaleString()}`}
            />
            <input
              required
              name="reason"
              minLength={5}
              className="input"
              placeholder="What it is for"
            />
            <button disabled={request.isLoading} className="btn btn-primary w-full">
              {request.isLoading ? "Sending…" : "Request advance"}
            </button>
          </form>
        )}
        {(advances.data ?? []).length > 0 && (
          <ul className="divide-y border-t text-sm">
            {(advances.data ?? []).map((advance) => (
              <li key={advance.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <div className="font-medium">{money(advance.amount)}</div>
                  <div className="text-xs text-gray-500">
                    {advance.created_at.slice(0, 10)} · {advance.reason}
                  </div>
                  {advance.rejection_reason && (
                    <div className="text-xs text-red-700">{advance.rejection_reason}</div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded bg-gray-100 px-2 py-1 text-xs">
                    {STAGE_LABELS[advance.stage]}
                  </span>
                  {["awaiting_approval", "approved"].includes(advance.stage) && (
                    <button
                      className="text-xs text-red-700"
                      onClick={() => cancel.mutate({ id: advance.id })}
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
